/**
 * Mexican and Spanish-Language Caribbean Music Styles, Instruments, and Vocal Styles Constants
 * 
 * Comprehensive collection of contemporary Mexican and Caribbean music genres (1950s-present)
 * for use in song generation and music style selection.
 */

import type { MusicStyle, InstrumentPreset, SearchableMusicItem } from './es-music-consts'
import { MexicanInstrument } from './es-music-consts'

// ============================================================================
// MUSIC STYLES - CARIBBEAN & CONTEMPORARY (1950s-PRESENT)
// ============================================================================

export const CARIBBEAN_CONTEMPORARY_MUSIC_STYLES: MusicStyle[] = [
  // Caribbean Genres
  {
    name: 'Salsa',
    description: 'Energetic dance music originating from Cuba and Puerto Rico, characterized by complex rhythms, brass sections, and call-and-response vocals. Popularized in New York in the 1960s-70s.',
    instruments: ['trumpet', 'trombone', 'piano', 'bass', 'conga', 'bongo', 'timbales', 'cowbell', 'güiro'],
    vocalStyle: 'energetic, rhythmic, call-and-response',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Caribbean (Cuba, Puerto Rico), New York',
    era: '1960s - present',
  },
  {
    name: 'Bachata',
    description: 'Romantic dance music from Dominican Republic featuring guitar, bongo, and güira. Evolved from bolero with distinctive syncopated rhythm. Became internationally popular in the 2000s.',
    instruments: ['guitar', 'bass', 'bongo', 'güira', 'maracas', 'keyboard'],
    vocalStyle: 'romantic, emotional, smooth',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Dominican Republic',
    era: '1960s - present',
  },
  {
    name: 'Merengue',
    description: 'Fast-paced dance music from Dominican Republic with distinctive two-beat rhythm. Features accordion, tambora, and güira. National dance of Dominican Republic.',
    instruments: ['accordion', 'tambora', 'güira', 'saxophone', 'trumpet', 'bass'],
    vocalStyle: 'energetic, rhythmic, festive',
    tempo: 'very fast',
    timeSignature: '2/4',
    publicDomainSongs: [],
    region: 'Dominican Republic',
    era: '1950s - present',
  },
  {
    name: 'Reggaeton',
    description: 'Urban music blending reggae, hip-hop, and Latin rhythms. Originated in Puerto Rico in the 1990s. Features dembow rhythm, electronic beats, and Spanish rap vocals.',
    instruments: ['drum machine', 'synthesizer', 'bass', 'keyboard', 'sampler'],
    vocalStyle: 'rap, rhythmic, urban',
    tempo: 'moderate to fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Puerto Rico, Latin America',
    era: '1990s - present',
  },
  {
    name: 'Latin Trap',
    description: 'Contemporary urban genre combining trap music with Latin rhythms and Spanish lyrics. Features heavy bass, hi-hats, and autotuned vocals.',
    instruments: ['drum machine', 'synthesizer', 'bass', '808', 'keyboard'],
    vocalStyle: 'autotuned, rhythmic, urban',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Puerto Rico, Latin America',
    era: '2010s - present',
  },
  {
    name: 'Cumbia Colombiana',
    description: 'Traditional Colombian dance music with African and indigenous influences. Features accordion, bass, and distinctive rhythm. Spread throughout Latin America.',
    instruments: ['accordion', 'bass', 'drums', 'conga', 'güiro', 'maracas'],
    vocalStyle: 'melodic, rhythmic, dance-oriented',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Colombia',
    era: '1950s - present',
  },
  {
    name: 'Vallenato',
    description: 'Traditional Colombian music featuring accordion, caja vallenata, and guacharaca. Storytelling music with romantic and narrative themes.',
    instruments: ['accordion', 'caja vallenata', 'guacharaca', 'bass'],
    vocalStyle: 'narrative, romantic, storytelling',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Colombia (Caribbean coast)',
    era: '1950s - present',
  },
  {
    name: 'Ska',
    description: 'Upbeat music with offbeat rhythm, originated in Jamaica but adapted in Latin America. Features horns, guitar, and energetic tempo.',
    instruments: ['trumpet', 'trombone', 'saxophone', 'guitar', 'bass', 'drums', 'keyboard'],
    vocalStyle: 'energetic, rhythmic, upbeat',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Jamaica, Latin America',
    era: '1960s - present',
  },
  {
    name: 'Rock en Español',
    description: 'Spanish-language rock music blending rock and roll with Latin influences. Emerged in the 1960s and gained international popularity in the 1990s.',
    instruments: ['electric guitar', 'bass guitar', 'drums', 'keyboard', 'synthesizer'],
    vocalStyle: 'powerful, rock, energetic',
    tempo: 'moderate to fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Latin America, Spain',
    era: '1960s - present',
  },
  {
    name: 'Latin Pop',
    description: 'Contemporary pop music sung in Spanish with international appeal. Features electronic production, catchy melodies, and modern arrangements.',
    instruments: ['synthesizer', 'drum machine', 'electric guitar', 'bass', 'keyboard', 'strings'],
    vocalStyle: 'smooth, modern pop, polished',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Latin America, Spain, United States',
    era: '1980s - present',
  },
  {
    name: 'Latin Jazz',
    description: 'Jazz music incorporating Latin rhythms and instruments. Features complex harmonies, improvisation, and Afro-Cuban influences.',
    instruments: ['piano', 'saxophone', 'trumpet', 'bass', 'conga', 'bongo', 'timbales'],
    vocalStyle: 'instrumental focus, scat singing when present',
    tempo: 'varies',
    timeSignature: '4/4, 3/4',
    publicDomainSongs: [],
    region: 'Cuba, United States, Latin America',
    era: '1940s - present',
  },
  {
    name: 'Son Cubano',
    description: 'Traditional Cuban music blending Spanish and African elements. Foundation of salsa music. Features tres, bongo, and maracas.',
    instruments: ['tres', 'bongo', 'maracas', 'bass', 'trumpet', 'clave'],
    vocalStyle: 'call-and-response, improvisational',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Cuba',
    era: '1920s - present',
  },
  {
    name: 'Rumba',
    description: 'Afro-Cuban music and dance with complex polyrhythms. Features percussion and call-and-response vocals. Three main styles: yambú, guaguancó, columbia.',
    instruments: ['conga', 'clave', 'maracas', 'cajón', 'bongo'],
    vocalStyle: 'call-and-response, improvisational, percussive',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Cuba',
    era: '19th century - present',
  },
  {
    name: 'Timba',
    description: 'Modern Cuban dance music combining salsa with funk, hip-hop, and jazz. Features complex arrangements and rapid tempo changes.',
    instruments: ['piano', 'bass', 'conga', 'timbales', 'trumpet', 'saxophone', 'keyboard'],
    vocalStyle: 'energetic, rapid, call-and-response',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Cuba',
    era: '1990s - present',
  },
  {
    name: 'Danzón',
    description: 'Elegant Cuban dance music with European and African influences. Features flute, violin, and piano. Precursor to mambo and cha-cha-chá.',
    instruments: ['flute', 'violin', 'piano', 'bass', 'timbales', 'güiro'],
    vocalStyle: 'elegant, smooth, instrumental focus',
    tempo: 'moderate',
    timeSignature: '2/4',
    publicDomainSongs: [],
    region: 'Cuba',
    era: '1870s - present',
  },
  {
    name: 'Mambo',
    description: 'Energetic Cuban dance music popularized in the 1940s-50s. Features big band arrangements with brass sections and complex rhythms.',
    instruments: ['trumpet', 'trombone', 'saxophone', 'piano', 'bass', 'conga', 'timbales'],
    vocalStyle: 'energetic, rhythmic, instrumental focus',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Cuba, United States',
    era: '1940s - present',
  },
  {
    name: 'Cha-cha-chá',
    description: 'Cuban dance music with distinctive three-beat rhythm. Popular in ballroom dancing. Features percussion and melodic instruments.',
    instruments: ['piano', 'bass', 'conga', 'güiro', 'maracas', 'trumpet'],
    vocalStyle: 'melodic, rhythmic, dance-oriented',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Cuba',
    era: '1950s - present',
  },
  {
    name: 'Plena',
    description: 'Folk music from Puerto Rico with African and Spanish influences. Features panderos (hand drums) and narrative lyrics about daily life.',
    instruments: ['pandero', 'güiro', 'maracas', 'bass'],
    vocalStyle: 'narrative, call-and-response, storytelling',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Puerto Rico',
    era: '1920s - present',
  },
  {
    name: 'Bomba',
    description: 'Traditional Puerto Rican music and dance with African roots. Features barrel drums and call-and-response between singer and drummers.',
    instruments: ['barril de bomba', 'cuá', 'maracas', 'bass'],
    vocalStyle: 'call-and-response, percussive, improvisational',
    tempo: 'moderate to fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Puerto Rico',
    era: '17th century - present',
  },
  {
    name: 'Latin Alternative',
    description: 'Alternative rock and indie music from Latin America blending rock, electronic, and traditional elements. Experimental and diverse.',
    instruments: ['electric guitar', 'bass', 'drums', 'synthesizer', 'keyboard', 'sampler'],
    vocalStyle: 'alternative, experimental, diverse',
    tempo: 'varies',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Latin America',
    era: '1990s - present',
  },
  {
    name: 'Latin Urban',
    description: 'Umbrella term for urban Latin music including reggaeton, Latin trap, and hip-hop. Features electronic production and Spanish rap.',
    instruments: ['drum machine', 'synthesizer', 'bass', '808', 'keyboard', 'sampler'],
    vocalStyle: 'rap, rhythmic, urban, autotuned',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Latin America, United States',
    era: '2000s - present',
  },
  {
    name: 'Bachata-Romántica',
    description: 'Romantic subgenre of bachata with smooth, emotional vocals and slower tempo. Popular in the 1990s-2000s.',
    instruments: ['guitar', 'bass', 'bongo', 'güira', 'keyboard', 'strings'],
    vocalStyle: 'romantic, emotional, smooth',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Dominican Republic',
    era: '1990s - present',
  },
  {
    name: 'Salsa Romántica',
    description: 'Romantic subgenre of salsa with slower tempo and emphasis on love ballads. Popular in the 1980s-90s.',
    instruments: ['piano', 'bass', 'conga', 'bongo', 'timbales', 'trumpet', 'strings'],
    vocalStyle: 'romantic, smooth, emotional',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Puerto Rico, New York',
    era: '1980s - present',
  },
  {
    name: 'Cumbia Villera',
    description: 'Argentine subgenre of cumbia with urban themes and electronic production. Features synthesizers and dance-oriented rhythms.',
    instruments: ['synthesizer', 'drum machine', 'bass', 'keyboard', 'güiro'],
    vocalStyle: 'rhythmic, urban, dance-oriented',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Argentina',
    era: '1990s - present',
  },
  {
    name: 'Tropical',
    description: 'Umbrella term for tropical Latin music including salsa, merengue, and cumbia. Popular at parties and celebrations.',
    instruments: ['trumpet', 'saxophone', 'piano', 'bass', 'conga', 'bongo', 'timbales'],
    vocalStyle: 'energetic, festive, dance-oriented',
    tempo: 'moderate to fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Caribbean, Latin America',
    era: '1950s - present',
  },
  {
    name: 'Latin Ballad',
    description: 'Slow, romantic ballads sung in Spanish. Features orchestral arrangements and emotional vocals. Popular across Latin America.',
    instruments: ['piano', 'strings', 'guitar', 'bass', 'keyboard'],
    vocalStyle: 'romantic, emotional, expressive',
    tempo: 'slow',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Latin America',
    era: '1950s - present',
  },
  {
    name: 'Latin Funk',
    description: 'Fusion of funk and Latin rhythms. Features electric bass, horns, and syncopated rhythms. Popular in the 1970s-80s.',
    instruments: ['electric bass', 'drums', 'trumpet', 'saxophone', 'keyboard', 'guitar'],
    vocalStyle: 'energetic, rhythmic, funk-oriented',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'United States, Latin America',
    era: '1970s - present',
  },
  {
    name: 'Latin House',
    description: 'Electronic dance music blending house music with Latin rhythms and Spanish vocals. Features four-on-the-floor beat and synthesizers.',
    instruments: ['drum machine', 'synthesizer', 'bass', 'keyboard', 'sampler'],
    vocalStyle: 'electronic, rhythmic, dance-oriented',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'United States, Latin America, Europe',
    era: '1990s - present',
  },
]

// ============================================================================
// CARIBBEAN INSTRUMENTS (Additional to Mexican Instruments)
// ============================================================================

export enum CaribbeanInstrument {
  // Percussion
  CONGA = 'conga',
  BONGO = 'bongo',
  TIMBALES = 'timbales',
  COWBELL = 'cowbell',
  CLAVE = 'clave',
  GÜIRA = 'güira',
  MARACAS = 'maracas',
  CAJON = 'cajón',
  TAMBORA = 'tambora',
  PANDERO = 'pandero',
  BARRIL_DE_BOMBA = 'barril de bomba',
  CUA = 'cuá',
  CAJA_VALLENATA = 'caja vallenata',
  GUACHARACA = 'guacharaca',
  
  // String Instruments
  TRES = 'tres',
  CUATRO = 'cuatro',
  
  // Wind Instruments
  TRUMPET = 'trumpet',
  TROMBONE = 'trombone',
  SAXOPHONE = 'saxophone',
  FLUTE = 'flute',
  
  // Electronic
  DRUM_MACHINE = 'drum machine',
  SYNTHESIZER = 'synthesizer',
  SAMPLER = 'sampler',
  EIGHT_OH_EIGHT = '808',
  
  // Other
  PIANO = 'piano',
  BASS = 'bass',
  KEYBOARD = 'keyboard',
  GUITAR = 'guitar',
}

export const CARIBBEAN_INSTRUMENT_DESCRIPTIONS: Record<CaribbeanInstrument, string> = {
  [CaribbeanInstrument.CONGA]: 'Tall hand drum essential to salsa and Latin music',
  [CaribbeanInstrument.BONGO]: 'Pair of small drums played with hands, used in salsa and son',
  [CaribbeanInstrument.TIMBALES]: 'Shallow drums played with sticks, essential to salsa',
  [CaribbeanInstrument.COWBELL]: 'Metal bell providing rhythmic accents in salsa',
  [CaribbeanInstrument.CLAVE]: 'Two wooden sticks providing the foundational rhythm in Latin music',
  [CaribbeanInstrument.GÜIRA]: 'Metal scraper used in merengue and bachata',
  [CaribbeanInstrument.MARACAS]: 'Shaken rattles providing rhythm in various Latin styles',
  [CaribbeanInstrument.CAJON]: 'Box drum played by hand, used in flamenco and Latin music',
  [CaribbeanInstrument.TAMBORA]: 'Large drum used in merengue',
  [CaribbeanInstrument.PANDERO]: 'Hand drum used in plena from Puerto Rico',
  [CaribbeanInstrument.BARRIL_DE_BOMBA]: 'Barrel drum used in bomba from Puerto Rico',
  [CaribbeanInstrument.CUA]: 'Wooden sticks played on side of bomba drum',
  [CaribbeanInstrument.CAJA_VALLENATA]: 'Small drum used in vallenato',
  [CaribbeanInstrument.GUACHARACA]: 'Scraped instrument used in vallenato',
  
  [CaribbeanInstrument.TRES]: 'Three-course guitar used in son cubano',
  [CaribbeanInstrument.CUATRO]: 'Four-string guitar from Puerto Rico',
  
  [CaribbeanInstrument.TRUMPET]: 'Brass instrument providing melodies in salsa and mambo',
  [CaribbeanInstrument.TROMBONE]: 'Brass instrument with slide, common in salsa',
  [CaribbeanInstrument.SAXOPHONE]: 'Woodwind instrument used in salsa and Latin jazz',
  [CaribbeanInstrument.FLUTE]: 'Woodwind instrument used in danzón and Latin jazz',
  
  [CaribbeanInstrument.DRUM_MACHINE]: 'Electronic drum machine used in reggaeton and Latin urban',
  [CaribbeanInstrument.SYNTHESIZER]: 'Electronic synthesizer used in modern Latin pop and reggaeton',
  [CaribbeanInstrument.SAMPLER]: 'Electronic sampler for creating beats and loops',
  [CaribbeanInstrument.EIGHT_OH_EIGHT]: 'Roland TR-808 drum machine, iconic in hip-hop and reggaeton',
  
  [CaribbeanInstrument.PIANO]: 'Keyboard instrument essential to salsa and Latin jazz',
  [CaribbeanInstrument.BASS]: 'Acoustic or electric bass providing rhythm foundation',
  [CaribbeanInstrument.KEYBOARD]: 'Electronic keyboard used in modern Latin music',
  [CaribbeanInstrument.GUITAR]: 'Acoustic or electric guitar used across Latin genres',
}

// ============================================================================
// CARIBBEAN VOCAL STYLES
// ============================================================================

export enum CaribbeanVocalStyle {
  ENERGETIC_RHYTHMIC = 'energetic, rhythmic, call-and-response',
  ROMANTIC_EMOTIONAL = 'romantic, emotional, smooth',
  RAP_RHYTHMIC = 'rap, rhythmic, urban',
  AUTOTUNED_URBAN = 'autotuned, rhythmic, urban',
  MELODIC_RHYTHMIC = 'melodic, rhythmic, dance-oriented',
  NARRATIVE_STORYTELLING = 'narrative, romantic, storytelling',
  POWERFUL_ROCK = 'powerful, rock, energetic',
  SMOOTH_MODERN = 'smooth, modern pop, polished',
  CALL_AND_RESPONSE = 'call-and-response, improvisational',
  PERCUSSIVE_IMPROVISATIONAL = 'call-and-response, percussive, improvisational',
  ELEGANT_SMOOTH = 'elegant, smooth, instrumental focus',
  ALTERNATIVE_EXPERIMENTAL = 'alternative, experimental, diverse',
  FESTIVE_DANCE = 'energetic, festive, dance-oriented',
  ROMANTIC_EXPRESSIVE = 'romantic, emotional, expressive',
  ELECTRONIC_RHYTHMIC = 'electronic, rhythmic, dance-oriented',
}

export const CARIBBEAN_VOCAL_STYLE_DESCRIPTIONS: Record<CaribbeanVocalStyle, string> = {
  [CaribbeanVocalStyle.ENERGETIC_RHYTHMIC]: 'Energetic vocals with call-and-response patterns, typical of salsa',
  [CaribbeanVocalStyle.ROMANTIC_EMOTIONAL]: 'Romantic, emotional vocals characteristic of bachata',
  [CaribbeanVocalStyle.RAP_RHYTHMIC]: 'Rap-style vocals in Spanish, characteristic of reggaeton',
  [CaribbeanVocalStyle.AUTOTUNED_URBAN]: 'Autotuned urban vocals typical of Latin trap',
  [CaribbeanVocalStyle.MELODIC_RHYTHMIC]: 'Melodic vocals with rhythmic elements, used in cumbia',
  [CaribbeanVocalStyle.NARRATIVE_STORYTELLING]: 'Narrative storytelling vocals characteristic of vallenato',
  [CaribbeanVocalStyle.POWERFUL_ROCK]: 'Powerful rock vocals in Spanish, used in rock en español',
  [CaribbeanVocalStyle.SMOOTH_MODERN]: 'Smooth, modern pop vocals characteristic of Latin pop',
  [CaribbeanVocalStyle.CALL_AND_RESPONSE]: 'Call-and-response vocals with improvisation, used in son cubano',
  [CaribbeanVocalStyle.PERCUSSIVE_IMPROVISATIONAL]: 'Percussive, improvisational vocals characteristic of bomba',
  [CaribbeanVocalStyle.ELEGANT_SMOOTH]: 'Elegant, smooth vocals for instrumental-focused music',
  [CaribbeanVocalStyle.ALTERNATIVE_EXPERIMENTAL]: 'Alternative, experimental vocals in Latin alternative',
  [CaribbeanVocalStyle.FESTIVE_DANCE]: 'Festive, dance-oriented vocals for tropical music',
  [CaribbeanVocalStyle.ROMANTIC_EXPRESSIVE]: 'Romantic, expressive vocals for Latin ballads',
  [CaribbeanVocalStyle.ELECTRONIC_RHYTHMIC]: 'Electronic, rhythmic vocals for Latin house',
}

// ============================================================================
// INSTRUMENT PRESETS - CARIBBEAN
// ============================================================================

export const CARIBBEAN_INSTRUMENT_PRESETS: InstrumentPreset[] = [
  {
    name: 'Salsa Classic',
    description: 'Traditional salsa ensemble',
    instruments: [
      MexicanInstrument.TRUMPET,
      MexicanInstrument.TROMBONE,
      MexicanInstrument.PIANO,
      MexicanInstrument.BASS,
      MexicanInstrument.CONGA,
      MexicanInstrument.BONGO,
      MexicanInstrument.TIMBALES,
    ],
    style: 'Salsa',
  },
  {
    name: 'Bachata Standard',
    description: 'Classic bachata instrumentation',
    instruments: [
      MexicanInstrument.GUITAR,
      MexicanInstrument.BASS,
      MexicanInstrument.BONGO,
      MexicanInstrument.KEYBOARD,
    ],
    style: 'Bachata',
  },
  {
    name: 'Merengue Full',
    description: 'Complete merengue ensemble',
    instruments: [
      MexicanInstrument.ACCORDION,
      MexicanInstrument.TAMBORA,
      MexicanInstrument.SAXOPHONE,
      MexicanInstrument.TRUMPET,
      MexicanInstrument.BASS,
    ],
    style: 'Merengue',
  },
  {
    name: 'Reggaeton Modern',
    description: 'Contemporary reggaeton production',
    instruments: [
      MexicanInstrument.SYNTHESIZER,
      MexicanInstrument.DRUMS,
      MexicanInstrument.BASS_GUITAR,
      MexicanInstrument.KEYBOARD,
    ],
    style: 'Reggaeton',
  },
  {
    name: 'Son Cubano',
    description: 'Traditional Cuban son ensemble',
    instruments: [
      MexicanInstrument.GUITAR,
      MexicanInstrument.BONGO,
      MexicanInstrument.MARACAS,
      MexicanInstrument.BASS,
      MexicanInstrument.TRUMPET,
    ],
    style: 'Son Cubano',
  },
]

// ============================================================================
// SEARCH HELPERS
// ============================================================================

export function getAllCaribbeanSearchableItems(): SearchableMusicItem[] {
  const styles: SearchableMusicItem[] = CARIBBEAN_CONTEMPORARY_MUSIC_STYLES.map(style => ({
    name: style.name,
    description: style.description,
    type: 'style',
    keywords: [
      ...style.name.toLowerCase().split(' '),
      ...style.description.toLowerCase().split(' '),
      ...style.instruments,
      ...(style.region ? [style.region.toLowerCase()] : []),
      ...(style.era ? [style.era.toLowerCase()] : []),
    ],
  }))

  const instruments: SearchableMusicItem[] = Object.values(CaribbeanInstrument).map(instrument => ({
    name: instrument,
    description: CARIBBEAN_INSTRUMENT_DESCRIPTIONS[instrument],
    type: 'instrument',
    keywords: [
      ...instrument.toLowerCase().split(' '),
      ...CARIBBEAN_INSTRUMENT_DESCRIPTIONS[instrument].toLowerCase().split(' '),
    ],
  }))

  const vocals: SearchableMusicItem[] = Object.values(CaribbeanVocalStyle).map(style => ({
    name: style,
    description: CARIBBEAN_VOCAL_STYLE_DESCRIPTIONS[style],
    type: 'vocal',
    keywords: [
      ...style.toLowerCase().split(' '),
      ...CARIBBEAN_VOCAL_STYLE_DESCRIPTIONS[style].toLowerCase().split(' '),
    ],
  }))

  const presets: SearchableMusicItem[] = CARIBBEAN_INSTRUMENT_PRESETS.map(preset => ({
    name: preset.name,
    description: preset.description,
    type: 'preset',
    keywords: [
      ...preset.name.toLowerCase().split(' '),
      ...preset.description.toLowerCase().split(' '),
      ...preset.instruments.map(i => i.toLowerCase()),
      preset.style.toLowerCase(),
    ],
  }))

  return [...styles, ...instruments, ...vocals, ...presets]
}

// ============================================================================
// COMBINED EXPORTS (for unified search)
// ============================================================================

export function getAllCombinedSearchableItems(): SearchableMusicItem[] {
  return [
    ...CARIBBEAN_CONTEMPORARY_MUSIC_STYLES.map(style => ({
      name: style.name,
      description: style.description,
      type: 'style' as const,
      keywords: [
        ...style.name.toLowerCase().split(' '),
        ...style.description.toLowerCase().split(' '),
        ...style.instruments,
        ...(style.region ? [style.region.toLowerCase()] : []),
        ...(style.era ? [style.era.toLowerCase()] : []),
      ],
    })),
    ...Object.values(CaribbeanInstrument).map(instrument => ({
      name: instrument,
      description: CARIBBEAN_INSTRUMENT_DESCRIPTIONS[instrument],
      type: 'instrument' as const,
      keywords: [
        ...instrument.toLowerCase().split(' '),
        ...CARIBBEAN_INSTRUMENT_DESCRIPTIONS[instrument].toLowerCase().split(' '),
      ],
    })),
    ...Object.values(CaribbeanVocalStyle).map(style => ({
      name: style,
      description: CARIBBEAN_VOCAL_STYLE_DESCRIPTIONS[style],
      type: 'vocal' as const,
      keywords: [
        ...style.toLowerCase().split(' '),
        ...CARIBBEAN_VOCAL_STYLE_DESCRIPTIONS[style].toLowerCase().split(' '),
      ],
    })),
  ]
}

